/**
 * Low Power Mode Detection Service
 * Detects iOS low power mode and other performance-related states
 */

/**
 * Check if the current device is iOS
 * @returns {Object} Device detection results
 */
export const checkDeviceType = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // Check for Windows first to avoid false positives
  const isWindows = /Windows/i.test(userAgent) || /Win32|Win64|Windows|WinCE/i.test(platform);
  
  // Check for Edge browser which might spoof iOS
  const isEdge = /Edg/i.test(userAgent) || /Edge/i.test(userAgent);
  
  // Check for Chrome/Chromium browsers
  const isChrome = /Chrome/i.test(userAgent) && !/Edg/i.test(userAgent);
  
  // Check for Firefox
  const isFirefox = /Firefox/i.test(userAgent);
  
  // Check for Safari (but not Chrome or Edge pretending to be Safari)
  const isSafari = /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent);
  
  // More accurate iOS detection - exclude desktop browsers
  const isiPhone = /iPhone/i.test(userAgent) && !isWindows && !isEdge && !isChrome && !isFirefox;
  const isiPad = /iPad/i.test(userAgent) && !isWindows && !isEdge && !isChrome && !isFirefox;
  const isiPod = /iPod/i.test(userAgent) && !isWindows && !isEdge && !isChrome && !isFirefox;
  
  // Additional check for iOS using platform and touch support
  const hasIOSPlatform = /iPhone|iPad|iPod/i.test(platform);
  const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Final iOS determination
  const isiOS = (isiPhone || isiPad || isiPod || hasIOSPlatform) && !isWindows && !isEdge;
  
  const deviceInfo = {
    isiPhone: isiPhone && isiOS,
    isiOS,
    isWindows,
    isEdge,
    isChrome,
    isFirefox,
    isSafari,
    userAgent,
    platform,
    hasTouchSupport
  };
  
  // console.log('Device detection results:', deviceInfo);
  
  return deviceInfo;
};

/**
 * Detect frame rate using requestAnimationFrame
 * @returns {Promise<number>} Frame rate in fps
 */
export const detectFrameRate = () => {
  return new Promise((resolve) => {
    let numFrames = 30;
    let startTime = performance.now();
    let i = 0;
    let tick = () => {
      if (i < numFrames) {
        i++;
        requestAnimationFrame(tick);
        return;
      }
      let frameRate = numFrames / ((performance.now() - startTime) / 1000);
      // console.log('Detected frame rate:', frameRate + 'fps');
      resolve(frameRate);
    };
    requestAnimationFrame(() => {
      tick();
    });
  });
};

/**
 * Detect iOS/iPad/macOS Low Power Mode, Chromium Energy Saver Mode, and maybe future Firefox power saving mode
 * @async
 * @method detectPowerSavingMode
 * @returns {boolean|undefined} return `undefined` if not sure
 */
export const detectPowerSavingMode = () => {
  // for iOS/iPadOS Safari, and maybe MacBook macOS Safari (not tested)
  if (/(iP(?:hone|ad|od)|Mac OS X)/.test(navigator.userAgent)) {
    // console.log('iOS/macOS device detected - using setInterval timing detection');
    // In Low Power Mode, cumulative delay effect happens on setInterval()
    return new Promise((resolve) => {
      let fps = 60;
      let interval = 1000 / fps;
      let numFrames = 30;
      let startTime = performance.now();
      let i = 0;
      let handle = setInterval(() => {
        if (i < numFrames) {
          i++;
          return;
        }
        clearInterval(handle);
        let actualInterval = (performance.now() - startTime) / numFrames;
        let ratio = actualInterval / interval; // 1.3x or more in Low Power Mode, 1.1x otherwise
       // console.log('Timing detection - Expected interval:', interval + 'ms', 'Actual interval:', actualInterval + 'ms', 'Ratio:', ratio);
        const isLowPowerMode = ratio > 1.3;
       // console.log('iOS Low Power Mode detected via timing:', isLowPowerMode);
        resolve(isLowPowerMode);
      }, interval);
    });
  }
  
  // for Safari, Chromium, and maybe future Firefox
  //console.log('Non-iOS device - using frame rate detection');
  return detectFrameRate().then((frameRate) => {
    // In Battery Saver Mode frameRate will be about 30fps or 20fps,
    // otherwise frameRate will be closed to monitor refresh rate (typically 60Hz)
    if (frameRate < 34) {
     // console.log('Low power mode detected via low frame rate:', frameRate + 'fps');
      return true;
    }
    // Fallback to regard as Low Power Mode when battery power is low (down to 20%)
    else if (navigator.getBattery) {
      return navigator.getBattery().then((battery) => {
        const isLowBattery = (!battery.charging && battery.level <= 0.2);
        //console.log('Battery fallback check - Level:', battery.level, 'Charging:', battery.charging, 'Low power:', isLowBattery);
        return isLowBattery ? true : false;
      });
    }
    //console.log('No low power indicators detected');
    return undefined;
  });
};

/**
 * Detect low power mode on iOS devices using timing-based detection
 * @returns {Promise<boolean>} True if low power mode is detected, false otherwise
 */
export const detectLowPowerMode = async () => {
  const deviceInfo = checkDeviceType();
  const { isiPhone, isiOS, 
   // isWindows, isEdge, isChrome, isFirefox
   } = deviceInfo;
  
  // Not an iOS device, no low power mode
  if (!isiPhone && !isiOS) {
    // console.log('Non-iOS device detected:', {
    //   isWindows,
    //   isEdge,
    //   isChrome,
    //   isFirefox,
    //   userAgent: deviceInfo.userAgent
    // });
    return false;
  }

  //console.log('iPhone/iOS device detected - running timing-based low power mode detection');

  try {
    // Use the proven timing-based detection method
    const result = await detectPowerSavingMode();
    
    if (result === true) {
      //console.log('Low Power Mode DETECTED via timing analysis');
      return true;
    } else if (result === false) {
      //console.log('Normal Mode DETECTED via timing analysis');
      return false;
    } else {
      //console.log('Timing detection uncertain, checking fallback indicators');
      
      // Fallback to traditional detection methods if timing detection is uncertain
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
     // console.log('Fallback - prefers-reduced-motion:', prefersReducedMotion);
      
      if (prefersReducedMotion) {
        //console.log('Low Power Mode detected via prefers-reduced-motion fallback');
        return true;
      }
      
      // Final fallback: default to normal mode
     // console.log('All detection methods uncertain - defaulting to normal mode');
      return false;
    }

  } catch (error) {
   // console.log('Error in timing-based low power mode detection, using fallback:', error);
    
    // Fallback to prefers-reduced-motion if timing detection fails
    try {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
     // console.log('Error fallback - prefers-reduced-motion:', prefersReducedMotion);
      return prefersReducedMotion;
    } catch (fallbackError) {
      //console.log('All detection methods failed, defaulting to normal mode:', fallbackError);
      return false;
    }
  }
};

/**
 * Set up listener for reduced motion preference changes (low power mode changes)
 * @param {Function} callback - Function to call when low power mode changes
 * @returns {Function} Cleanup function to remove the listener
 */
export const setupLowPowerModeListener = (callback) => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  
  const handleReducedMotionChange = async (event) => {
    //console.log('Reduced motion preference changed:', event.matches);
    
    if (event.matches) {
      // Reduced motion enabled = low power mode
      callback(true);
    } else {
      // Reduced motion disabled = re-check for low power mode
      const isLowPowerMode = await detectLowPowerMode();
      callback(isLowPowerMode);
    }
  };

  // Enhanced iPhone detection for dynamic changes
  const handleVisibilityChange = async () => {
    if (!document.hidden) {
      // Re-run the full detection when page becomes visible
      const deviceInfo = checkDeviceType();
      if (deviceInfo.isiPhone) {
       // console.log('Page visible - re-checking iPhone low power mode');
        try {
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (prefersReducedMotion) {
           // console.log('Low power mode detected on visibility change');
            callback(true);
          }
        } catch (error) {
          //console.log('Error in visibility change detection:', error);
        }
      }
    }
  };

  // Re-check on focus (when user returns to app) 
  const handleFocus = async () => {
    const deviceInfo = checkDeviceType();
    if (deviceInfo.isiPhone) {
     // console.log('Window focused - re-checking iPhone low power mode');
      try {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          //console.log('Low power mode detected on focus');
          callback(true);
        }
      } catch (error) {
        //console.log('Error in focus detection:', error);
      }
    }
  };

  // Add all listeners with proper browser support
  if (typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
  } else if (typeof reducedMotionQuery.addListener === 'function') {
    reducedMotionQuery.addListener(handleReducedMotionChange);
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleFocus);

  // Return cleanup function
  return () => {
    if (typeof reducedMotionQuery.removeEventListener === 'function') {
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
    } else if (typeof reducedMotionQuery.removeListener === 'function') {
      reducedMotionQuery.removeListener(handleReducedMotionChange);
    }
    
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleFocus);
  };
};

/**
 * Get low power mode detection result with caching
 * @param {boolean} forceRefresh - Force a fresh detection instead of using cache
 * @returns {Promise<Object>} Detection result with metadata
 */
export const getLowPowerModeStatus = async (forceRefresh = false) => {
  const cacheKey = 'lowPowerModeDetection';
  const cacheTimeKey = 'lowPowerModeDetectionTime';
  const cacheValidityMs = 30000; // 30 seconds cache validity

  // Check cache if not forcing refresh
  if (!forceRefresh) {
    const cachedResult = sessionStorage.getItem(cacheKey);
    const cachedTime = sessionStorage.getItem(cacheTimeKey);
    
    if (cachedResult && cachedTime) {
      const timeDiff = Date.now() - parseInt(cachedTime);
      if (timeDiff < cacheValidityMs) {
       // console.log('Using cached low power mode detection result');
        return JSON.parse(cachedResult);
      }
    }
  }

  // Perform fresh detection
  const startTime = Date.now();
  const isLowPowerMode = await detectLowPowerMode();
  const detectionTime = Date.now() - startTime;
  const deviceInfo = checkDeviceType();
  const { isiPhone, isiOS, isWindows, isEdge, isChrome, isFirefox, isSafari } = deviceInfo;

  // Determine device type more accurately
  let deviceType = 'other';
  if (isiPhone) {
    deviceType = 'iPhone';
  } else if (isiOS) {
    deviceType = 'iOS';
  } else if (isWindows) {
    if (isEdge) {
      deviceType = 'Windows-Edge';
    } else if (isChrome) {
      deviceType = 'Windows-Chrome';
    } else if (isFirefox) {
      deviceType = 'Windows-Firefox';
    } else {
      deviceType = 'Windows';
    }
  }

  const result = {
    isLowPowerMode,
    isIOSDevice: isiPhone || isiOS,
    deviceType,
    detectionTime,
    timestamp: Date.now(),
    deviceInfo: {
      isWindows,
      isEdge,
      isChrome,
      isFirefox,
      isSafari,
      userAgent: deviceInfo.userAgent,
      platform: deviceInfo.platform
    }
  };

  // Cache the result
  sessionStorage.setItem(cacheKey, JSON.stringify(result));
  sessionStorage.setItem(cacheTimeKey, result.timestamp.toString());

  //console.log('Low power mode detection completed:', result);
  return result;
};

/**
 * Manual test function for timing-based low power mode detection
 * Call this from browser console: window.testTimingDetection()
 */
export const testTimingDetection = async () => {
  //console.log('=== Manual Timing-Based Low Power Mode Test ===');
  
  //console.log('1. User Agent:', navigator.userAgent);
  //console.log('2. iOS Pattern Match:', /(iP(?:hone|ad|od)|Mac OS X)/.test(navigator.userAgent));
  //console.log('3. prefers-reduced-motion:', window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  
 // console.log('Running timing detection...');
  const timingResult = await detectPowerSavingMode();
  //console.log('Timing detection result:', timingResult);
  
  //console.log('Running frame rate detection...');
  const frameRate = await detectFrameRate();
  //console.log('Frame rate:', frameRate + 'fps');
  
  const finalResult = await detectLowPowerMode();
  //console.log('Final low power mode result:', finalResult);
  
  return {
    timingResult,
    frameRate,
    finalResult
  };
};

/**
 * Debug function to log detailed device information
 * @returns {Object} Detailed device information for debugging
 */
export const debugDeviceDetection = () => {
  const deviceInfo = checkDeviceType();
  const debugInfo = {
    ...deviceInfo,
    windowsMatches: /Windows/i.test(navigator.userAgent),
    edgeMatches: /Edg/i.test(navigator.userAgent) || /Edge/i.test(navigator.userAgent),
    chromeMatches: /Chrome/i.test(navigator.userAgent),
    firefoxMatches: /Firefox/i.test(navigator.userAgent),
    safariMatches: /Safari/i.test(navigator.userAgent),
    iPhoneMatches: /iPhone/i.test(navigator.userAgent),
    iPadMatches: /iPad/i.test(navigator.userAgent),
    iPodMatches: /iPod/i.test(navigator.userAgent),
    reducedMotionEnabled: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    touchSupport: 'ontouchstart' in window,
    maxTouchPoints: navigator.maxTouchPoints,
    navigatorPlatform: navigator.platform,
    navigatorUserAgent: navigator.userAgent
  };
  
  console.table(debugInfo);
  return debugInfo;
};

// Make test functions available globally for browser console testing
if (typeof window !== 'undefined') {
  window.testTimingDetection = testTimingDetection;
  window.debugDeviceDetection = debugDeviceDetection;
}